import {put, call} from 'redux-saga/effects';
import ImportSource from "../../entities/importSource";
import * as Actions from "../actions/importSourceActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ImportSourceAction = {
    importSourceId: string;
    importSource: ImportSource;
    importSources: ImportSource[];
};

export function* fetchOneImportSource(action: ImportSourceAction) {
	try {
		const importSource: ImportSource = yield call(api.findOne, 'importSource', action.importSourceId);

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_FETCH_ONE_SUCCESS, importSourceId: action.importSourceId, importSource});
	}
	catch(err) {
		yield put({type: Actions.IMPORTSOURCE_FETCH_ONE_FAILURE, importSourceId: action.importSourceId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ImportSourceAction} action
 */
export function* saveImportSource(action: ImportSourceAction) {
    // console.debug('⮂ IMPORTSOURCE_SAVE saga', action.importSource?.id);
    
	if (action.importSource === undefined || action.importSource === null) {
		console.error('saveImportSource received a null');
		throw new Error('saveImportSource received a null');
	}

	if (!(action.importSource instanceof ImportSource)) {
		action.importSource = new ImportSource(action.importSource);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'importSource', action.importSource);
		if (addResult.isErr()) {
			console.error(`⮂ saveImportSource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTSOURCE_SAVE_FAILURE, importSource: action.importSource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_SAVE, importSource: action.importSource});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'importSource', action.importSource.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.IMPORTSOURCE_SAVE_FAILURE, importSource: action.importSource, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {ImportSourceAction} action
 * @param {ImportSource[]} action.importSources
 */
export function* saveManyImportSources(action: ImportSourceAction) {
    // console.debug('⮂ IMPORTSOURCE_SAVE_MANY saga', action.importSources.map(importSource => importSource.id).join(','));
    
    if (!Array.isArray(action.importSources)) {
        console.error('saveManyImportSources expected an array, received', action.importSources);
        throw new Error('saveManyImportSources expected an array');
    }

	try {
		const toSave: ImportSource[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.importSources.length; i++) {
			const importSource = action.importSources[i];
			importSource.persisted = false;
			toSave.push(importSource);
		}

		const addResult = yield call(idb.add, 'importSource', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyImportSources: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTSOURCE_SAVE_MANY_FAILURE, importSources: action.importSources, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_SAVE_MANY, importSources: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.IMPORTSOURCE_SAVE_MANY_FAILURE, importSources: action.importSources, status: 500, err});

		console.error(`Error saving ${action.importSources.length} importSources to database`, err);
	}
}

/**
 * @param {ImportSourceAction} action
 */
export function* removeImportSource(action: ImportSourceAction) {
    const userId = getCurrentUserId();
	if (!userId || action.importSource === undefined || action.importSource === null || !(action.importSource instanceof ImportSource)) {
			console.error('removeImportSource expected type ImportSource, received', action.importSource);
			throw new Error('removeImportSource expected type ImportSource');
		}

	try {
		// Mark as deleted
		action.importSource.status = 'D';
		action.importSource.addedOn = newHRTimestamp();
		action.importSource.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'importSource', action.importSource);
		if (addResult.isErr()) {
			console.error(`⮂ removeImportSource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTSOURCE_REMOVE_FAILURE, importSource: action.importSource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_REMOVE, importSource: action.importSource});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.IMPORTSOURCE_REMOVE_FAILURE, importSource: action.importSource, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this ImportSource.
	*
	* @param {ImportSourceAction} action
	*/
export function* restoreImportSource(action: ImportSourceAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreImportSource expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.importSource === undefined || action.importSource === null) {
        console.error('restoreImportSource expected a ImportSource, received null');
        throw new Error('restoreImportSource expected a ImportSource, received null');
    }
    else if (!(action.importSource instanceof ImportSource)) action.importSource = new ImportSource(action.importSource); 
    
	try {
		// Mark as restored
		action.importSource.status = 'U';
		action.importSource.addedOn = newHRTimestamp();
		action.importSource.addedBy = userId;
		action.importSource.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'importSource', action.importSource);
		if (addResult.isErr()) {
			console.error(`⮂ restoreImportSource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.IMPORTSOURCE_RESTORE_FAILURE, importSource: action.importSource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.IMPORTSOURCE_RESTORE, importSource: action.importSource});
	}
	catch(err) {
		yield put({type: Actions.IMPORTSOURCE_RESTORE_FAILURE, importSource: action.importSource, status: 500, err});

		console.error('Error restoring from database', err);
	}
}